import DOMPurify from "dompurify";

import { getQuillHTML } from "#/shared/lib/quill-html";

import { isLossless } from "./html-fidelity";

/**
 * The DOMPurify policy for hand-written HTML that is parsed in the admin page. It strips active content
 * and keeps the tags and attributes Sparkle's blots read, which DOMPurify drops by default.
 */
export const SANITIZE_POLICY = { ADD_TAGS: ["sparkle-tel", "snippet"], ADD_ATTR: ["target", "draggable"] };

/**
 * Gives a WYSIWYG field an "Edit HTML" mode, in which the field's HTML is edited as text and is
 * stored without passing through Quill, minus the active content `SANITIZE_POLICY` removes. Content
 * Quill cannot represent stays in this mode: it opens there, and leaving it asks before Quill strips
 * anything.
 *
 * Installs `field.wysiwygHtmlMode`, next to `field.wysiwyg`, for code that reads or writes the
 * field from outside:
 *   - isActive():      whether the field is in HTML mode
 *   - value():         the field's HTML in either mode
 *   - setValue(html):  replaces the field's HTML without announcing it, entering HTML mode when Quill
 *                      cannot represent it
 *
 * @param {object} options
 * @param {HTMLElement} options.field the textarea the form submits
 * @param {Quill} options.editor the field's Quill instance
 * @param {HTMLElement} options.container the rendered wysiwyg-field-template
 * @param {function(): string} options.read returns the HTML stored in the field
 * @param {function(string, {announce?: boolean}=): void} options.write stores HTML in the field and
 *   announces a change, unless `announce` is false
 * @returns {{ load: function(string): void }} `load` fills the editor with the field's saved HTML
 */
export function attachHtmlMode({ field, editor, container, read, write }) {
  const toggle = container.querySelector(".wysiwyg-html-mode__toggle");
  const source = container.querySelector(".wysiwyg-html-mode__source");
  const unsupportedNotice = container.querySelector(".wysiwyg-html-mode__notice.--unsupported");
  const lossyNotice = container.querySelector(".wysiwyg-html-mode__notice.--lossy");
  let active = false;

  // Loads `html` into Quill without announcing it, and returns whether Quill kept all of it. Quill's
  // clipboard parses its input in the live page, where an event handler such as `onerror` runs, so it
  // gets a sanitized copy. Anything the sanitizer strips counts as lost, because the comparison is
  // against `html` itself.
  function convert(html) {
    editor.setContents(editor.clipboard.convert({ html: DOMPurify.sanitize(html, SANITIZE_POLICY) }), "silent");

    return isLossless(html, getQuillHTML(editor));
  }

  function showNotice(notice) {
    unsupportedNotice.hidden = notice !== unsupportedNotice;
    lossyNotice.hidden = notice !== lossyNotice;
  }

  function enter(html, notice = null) {
    active = true;
    source.value = html;
    source.hidden = false;
    container.classList.add("--html-mode");
    toggle.setAttribute("aria-pressed", "true");
    showNotice(notice);
  }

  // Hands the field back to Quill and stores what Quill holds. Quill's history records silent changes
  // too, so it is cleared: undoing the conversions would store HTML the user left behind.
  function leave() {
    active = false;
    source.hidden = true;
    container.classList.remove("--html-mode");
    toggle.setAttribute("aria-pressed", "false");
    showNotice(null);
    editor.getModule("history")?.clear();
    write(getQuillHTML(editor));
  }

  function setVisualContent(html) {
    if (convert(html)) {
      write(getQuillHTML(editor), { announce: false });
    } else {
      enter(html, unsupportedNotice);
      write(html, { announce: false });
    }
  }

  // Fills the editor with the field's saved HTML. When Quill keeps all of it, stores Quill's HTML in
  // the field without announcing it. Otherwise opens in HTML mode and leaves the saved HTML untouched.
  function load(html) {
    if (convert(html)) {
      write(getQuillHTML(editor), { announce: false });
    } else {
      enter(html, unsupportedNotice);
    }
  }

  function toggleMode() {
    if (!active) {
      enter(read());
      source.focus();
      return;
    }

    if (convert(source.value)) {
      leave();
    } else {
      showNotice(lossyNotice);
    }
  }

  function switchAnyway() {
    convert(source.value);
    leave();
    editor.focus();
  }

  function keepEditing() {
    showNotice(null);
    source.focus();
  }

  // Only the field announces changes: listeners bound to the form must see its events, never the
  // events of this unnamed textarea.
  function handleSourceInput(event) {
    event.stopPropagation();
    showNotice(null);
    write(source.value);
  }

  // Removes active content, such as a pasted `<script>` or an `onerror` handler, once the editor leaves
  // the source. Typing is left alone, because half-typed HTML is not valid yet. DOMPurify also
  // reformats what it keeps, so the text is replaced only when something was removed.
  function sanitizeSource(event) {
    event.stopPropagation();

    const sanitized = DOMPurify.sanitize(source.value, SANITIZE_POLICY);
    if (DOMPurify.removed.length > 0) {
      source.value = sanitized;
      write(sanitized);
    }
  }

  // Quill's `disable()` calls `enable(false)` on the instance, so this covers both.
  const enable = editor.enable.bind(editor);
  editor.enable = function enableWithSource(enabled = true) {
    enable(enabled);
    source.readOnly = !enabled;
    toggle.disabled = !enabled;
    // The warning's "Switch anyway" writes the field, so a disabled editor closes it.
    if (!enabled) {
      lossyNotice.hidden = true;
    }
  };

  toggle.addEventListener("click", toggleMode);
  lossyNotice.querySelector(".wysiwyg-html-mode__switch-anyway").addEventListener("click", switchAnyway);
  lossyNotice.querySelector(".wysiwyg-html-mode__keep-editing").addEventListener("click", keepEditing);
  source.addEventListener("input", handleSourceInput);
  source.addEventListener("change", sanitizeSource);

  field.wysiwygHtmlMode = {
    isActive() {
      return active;
    },
    value() {
      return active ? source.value : read();
    },
    setValue(html) {
      if (active) {
        source.value = html;
        write(html, { announce: false });
      } else {
        setVisualContent(html);
      }
    },
  };

  return { load };
}
